import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FunnyLoader } from "@/components/FunnyLoader";

export const HeroSection = ({ banners = [], loading }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Filter active banners
  const activeBanners = banners.filter(b => b.active);
  
  const nextSlide = useCallback(() => {
    if (activeBanners.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % activeBanners.length);
  }, [activeBanners.length]);

  const prevSlide = useCallback(() => {
    if (activeBanners.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + activeBanners.length) % activeBanners.length);
  }, [activeBanners.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || activeBanners.length <= 1) return;
    
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, activeBanners.length]);

  // Reset slide if banners change
  useEffect(() => {
    if (currentSlide >= activeBanners.length) {
      setCurrentSlide(0);
    }
  }, [activeBanners.length, currentSlide]);

  if (loading) {
    return (
      <section className="relative w-full h-[215px] md:h-[430px] bg-gradient-to-br from-gray-100 to-gray-200 mt-[80px] flex items-center justify-center" data-testid="hero-loading">
        <FunnyLoader />
      </section>
    );
  }

  // No banners - show placeholder with fun message
  if (activeBanners.length === 0) {
    return (
      <section 
        className="relative w-full h-[215px] md:h-[430px] bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] mt-[80px] flex items-center justify-center"
        data-testid="hero-section"
      >
        <div className="text-center">
          <div className="text-6xl mb-4">🎬</div>
          <p className="text-xl text-white font-bold">¡Próximamente más desmadre!</p>
          <p className="text-sm mt-2 text-white/60">Los banners están de vacaciones</p>
        </div>
      </section>
    );
  }

  const current = activeBanners[currentSlide];
  
  // Get the appropriate image URL based on screen size
  const getImageUrl = (banner) => {
    if (isMobile && banner.mobile_image_url) {
      return banner.mobile_image_url;
    }
    return banner.image_url;
  };

  const handleBannerClick = () => {
    if (current?.link_url) {
      window.open(current.link_url, '_blank');
    }
  };

  return (
    <section 
      className="relative w-full h-[215px] md:h-[430px] overflow-hidden mt-[80px]"
      data-testid="hero-section"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Slides */}
      <div 
        className="flex h-full transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {activeBanners.map((banner) => (
          <div
            key={banner.id}
            className={`relative w-full h-full flex-shrink-0 ${banner.link_url ? 'cursor-pointer' : ''}`}
            onClick={banner.link_url ? handleBannerClick : undefined}
          >
            {/* Banner Image */}
            <img 
              src={getImageUrl(banner)}
              alt="Banner"
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {activeBanners.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors z-20"
            data-testid="carousel-prev"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors z-20"
            data-testid="carousel-next"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {activeBanners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {activeBanners.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-[#E91E8C] w-8' 
                  : 'bg-white/50 hover:bg-white/80 w-2'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
